import { html, TemplateResult } from "lit-html";
import { getDocLocale } from "Helper/metaHelper";
import { I18nService } from "Services/I18nService";

/**
 * Helper, converts UTC date to DD.MM.YYYY
 * @param {string} date
 * @returns {string}
 */
function formatDate(date: string) {
  if (!date) return "";

  const locale = getDocLocale();
  const options = { year: "numeric", month: "2-digit", day: "2-digit" } as const;
  const dateIn = new Date(date);
  return dateIn.toLocaleDateString(locale, options);
}

/**
 * Helper, increases date by certain amount of days
 * @param {string} date
 * @param {number} days
 * @returns {string}
 */
function increaseDate(date: string, days: number) {
  if (!date) return "";

  const locale = getDocLocale();
  const options = { year: "numeric", month: "2-digit", day: "2-digit" } as const;
  const dateIn = new Date(date);
  dateIn.setDate(dateIn.getDate() + days);
  return dateIn.toLocaleDateString(locale, options);
}

/**
 * Helper, converts number and currency to price tag
 * @param {number} price
 * @param {string} currency
 * @returns {string}
 */
function formatPrice(price: number, currency: string): string {
  const locale = getDocLocale();
  const formatter = new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
  });
  return formatter.format(price);
}

/**
 * Helper, converts country code to country name
 * @param {string} countryCode
 * @returns {string}
 */
function getCountry(countryCode?: string): string | undefined {
  if (!countryCode) return "";

  const locale = getDocLocale();
  const regionNames = new Intl.DisplayNames(locale, { type: "region" });
  return regionNames.of(countryCode);
}

/** ####### RENDERER ####### */
/**
 * Render order item with all relevant entries
 * @param {string} orderDate
 * @param {string} orderNumber
 * @param {string} price
 * @param {string} orderStatus
 * @param {function} showOrderData
 * @returns {TemplateResult}
 */
// eslint-disable-next-line max-params
function renderListEntry(
  orderDate: string,
  orderNumber: string,
  price: string,
  orderStatus: TemplateResult,
  showOrderData: Function,
): TemplateResult {
  return html`
    <dl class="fra-m-orders-list">
      <div class="fra-e-list__entry">
        <dt class="fra-e-list__label">${orderDate}</dt>
        <dt class="fra-e-list__label fra-e-aggregate fra-h-hide-mq2">
          <span>${orderNumber}</span>
          <span>${price}</span>
          ${orderStatus}
        </dt>
        <dt class="fra-e-list__label fra-e-list__label--order-number fra-h-show-mq2">${orderNumber}</dt>
        <dt class="fra-e-list__label fra-e-list__label--price fra-h-show-mq2">${price}</dt>
        <dt class="fra-e-list__label fra-h-show-mq2">${orderStatus}</dt>
        <dd class="fra-e-list__desc fra-e-list__desc-link ">
          <a
            class="fra-e-link fra-e-link--with-icon fra-e-link--animated-right"
            @click=${(e: UIEvent) => {
              e.preventDefault();
              showOrderData(orderNumber);
            }}
          >
            <fra-e-icon class="fra-e-icon " icon-id="fra-icon-arrow-right"></fra-e-icon>
          </a>
        </dd>
      </div>
    </dl>
  `;
}

/**
 * Render order details list with all relevant entries
 * @param {Record<string, any>} order
 * @returns {TemplateResult}
 */
function renderOrderDetailsList(order: Record<string, any>): TemplateResult {
  return html`
    <fra-e-list class="fra-e-list">
      <dl class="fra-m-orders__details--list">
        <div class="fra-e-list__entry ">
          <dt class="fra-e-list__label">
            ${I18nService.getInstance().getKey("fraport.travel.components.orders.orderDate.label")}
          </dt>
          <dd class="fra-e-list__desc fra-e-list__desc-link ">${formatDate(order.orderDate)}</dd>
        </div>
        ${order.orderStatus !== "CANCELLED" ? renderCollectDate(order) : ""}
        <div class="fra-e-list__entry ">
          <dt class="fra-e-list__label">
            ${I18nService.getInstance().getKey("fraport.travel.components.orders.status.label")}
          </dt>
          <dd class="fra-e-list__desc fra-e-list__desc-link ">${renderState(order.orderStatus)}</dd>
        </div>
        ${order.orderStatus === "COMPLETED" &&
        order.shipmentRequest &&
        order.shipmentRequest.shipment.parcels[0].trackingNumber
          ? html`
              <div class="fra-e-list__entry ">
                <dt class="fra-e-list__label">
                  ${I18nService.getInstance().getKey("fraport.travel.components.orders.trackingNumber.label")}
                </dt>
                <dd class="fra-e-list__desc fra-e-list__desc-link">
                  <a
                    class="fra-e-list__desc--tracking-link"
                    href="https://www.dhl.de/de/privatkunden/dhl-sendungsverfolgung.html?piececode=${order
                      .shipmentRequest.shipment.parcels[0].trackingNumber}"
                    target="_blank"
                  >
                    ${order.shipmentRequest.shipment.parcels[0].trackingNumber}
                  </a>
                </dd>
              </div>
            `
          : ""}
        ${order.orderStatus !== "CANCELLED" &&
        order.lines.filter((product: Record<string, any>) => product.deliveryReference === "delivery____domestichome")
          .length !== 0
          ? html`
              <div class="fra-e-list__entry ">
                <dt class="fra-e-list__label">
                  ${I18nService.getInstance().getKey("fraport.travel.components.orders.paymentMethod.label")}
                </dt>
                <dd class="fra-e-list__desc fra-e-list__desc-link ">
                  ${getPaymentMethod(order.payment.methodsOfPayment)}
                </dd>
              </div>
            `
          : ""}
        ${order.orderStatus !== "CANCELLED"
          ? html`
              ${renderSum(order)}
            `
          : ""}
      </dl>
    </fra-e-list>
  `;
}

/**
 * Renders order-customer-address
 *
 * @param {Record<string, any>} billingAddress
 * @param {string} category
 * @param {string} status
 * @param {string} invoiceUrl
 * @returns {TemplateResult}
 */
function renderAddress(
  billingAddress: Record<string, any>,
  category: string,
  status: string,
  invoiceUrl?: string,
): TemplateResult {
  if (billingAddress !== undefined) {
    return html`
      <div class="fra-m-orders__info">
        <h4 class="fra-h4">
          <span>${I18nService.getInstance().getKey(`fraport.travel.components.orders.address.${category}`)}</span>
        </h4>
        <span>${billingAddress.firstName} ${billingAddress.lastName}</span>
        <br />
        <span>${billingAddress.street} ${billingAddress.houseNumber}</span>
        <br />
        <span>${billingAddress.zip} ${billingAddress.city}</span>
        <br />
        <span>${getCountry(billingAddress.countryCode)}</span>
        <br />
      </div>
      ${status === "COMPLETED" ? renderCTA(category, invoiceUrl) : ""}
    `;
  }
  return html``;
}

/**
 * shown if order status is cancelled
 * @returns {TemplateResult}
 */
function renderCancelledMessage(): TemplateResult {
  return html`
    <div class="fra-m-orders__data-wrapper--cancelled-message">
      <fra-e-icon class="fra-e-icon fra-e-button__icon" icon-id="fra-icon-notification"></fra-e-icon>
      <div>${I18nService.getInstance().getKey("fraport.travel.components.orders.cancelledMessage")}</div>
    </div>
  `;
}

/**
 * renders buttons under address
 * @param {string} category
 * @param {string} invoiceUrl
 * @returns {TemplateResult}
 */
function renderCTA(category: string, invoiceUrl?: string): TemplateResult {
  if (category === "billing" && invoiceUrl) {
    const buttonText = I18nService.getInstance().getKey("fraport.travel.components.orders.address.downloadInvoice");
    return html`
      <div class="fra-m-orders__action">
        <a class="fra-e-button fra-m-orders__button" href="${invoiceUrl}" target="_blank">
          <span class="fra-e-button__label">${buttonText}</span>
        </a>
      </div>
    `;
  } else if (category === "delivery" && invoiceUrl) {
    const buttonText = I18nService.getInstance().getKey("fraport.travel.components.orders.address.cancelOrder");
    return html`
      <div class="fra-m-orders__action">
        <a
          class="fra-e-button fra-m-orders__button"
          href="https://www.dhl.de/retoure/gw/rpcustomerweb/OrderEntry.action?hash=5bee846506eee4d96d0c4d808be75f914cb8524338e98469a575f5bee609259a"
          target="_blank"
        >
          <span class="fra-e-button__label">${buttonText}</span>
        </a>
      </div>
    `;
  }

  return html``;
}

/**
 * renders delivery method for home delivery and click & collect
 * @param {string} method
 * @returns {TemplateResult}
 */
function renderDeliveryMethod(method: string): TemplateResult {
  let deliveryText;
  switch (method) {
    case "hd":
      deliveryText = I18nService.getInstance().getKey("fraport.travel.components.orders.delivery.germany");
      break;
    case "ccPassenger":
      deliveryText = I18nService.getInstance().getKey("fraport.travel.components.orders.delivery.ccPassenger");
      break;
    case "ccNonPassenger":
      deliveryText = I18nService.getInstance().getKey("fraport.travel.components.orders.delivery.ccNonPassenger");
      break;
    case "ccCrewshop":
      deliveryText = I18nService.getInstance().getKey("fraport.travel.components.orders.delivery.ccCrewshop");
      break;
    default:
      deliveryText = "No valid delivery method found";
      break;
  }

  return html`
    <div class="fra-m-orders__delivery-method">
      ${I18nService.getInstance().getKey("fraport.travel.components.orders.delivery.deliveryMethod")}: ${deliveryText}
      <hr />
    </div>
  `;
}

/**
 * renders product with corresponding state and price
 * @param {Record<string, any>} product
 * @param {string} orderStatus
 * @param {number} amount
 * @returns {TemplateResult}
 */
function renderProduct(product: Record<string, any>, orderStatus: string, amount: number): TemplateResult {
  let showReducedPrice = false;
  if (product.price.discounts[0] && product.price.discounts[0].discountAmount.value > 0) {
    showReducedPrice = true;
  }
  return html`
    <div class="fra-m-orders__product">
      <fra-e-lazy-image
        class="fra-e-lazy-image"
        src="${product.product.imageUrl}"
        alt="Product image"
        aspect-ratio="contain"
      ></fra-e-lazy-image>
      <div class="fra-m-orders__product-info">
        <div class="fra-m-orders__product-info--description">
          <div class="fra-m-orders__product-info--headline">${product.name}</div>
          <div class="fra-m-orders__product-info--details">
            ${I18nService.getInstance().getKey("fraport.travel.components.orders.delivery.amount")}: ${amount}
          </div>
          <div class="fra-m-orders__product-info--details">
            ${renderProductState(product.status, orderStatus, product.deliveryReference)}
          </div>
        </div>
        ${orderStatus !== "CANCELLED" && product.status !== "CANCELLED"
          ? html`
              <div class="fra-m-orders__product-info--price">
                ${showReducedPrice
                  ? renderOriginalPrice(
                      (product.price.gross.value + product.price.discounts[0].discountAmount.value) * amount,
                      product.price.gross.currency,
                    )
                  : ""}
                ${formatPrice(product.price.gross.value * amount, product.price.gross.currency)}
              </div>
            `
          : ""}
      </div>
    </div>
  `;
}

/**
 * calculates total sum of shipped products
 * @param {Record<string, any>} products
 * @param {string} orderStatus
 * @returns {number}
 */
function calculateTotalSum(products: Record<string, any>, orderStatus: string): number {
  let totalSum = 0;
  products.forEach((product: Record<string, any>) => {
    // only removes cancelled products from sum if status is not LOGISTIC_PROGRESS
    if (orderStatus === "LOGISTIC_PROGRESS" || product.status !== "CANCELLED") {
      totalSum += product.price.gross.value;
    }
  });
  return totalSum;
}

/**
 * calculates sum of online paid products
 * @param {Record<string, any>} products
 * @param {Record<string, any>} paymentMethods
 * @param {string} orderStatus
 * @returns {number}
 */
function calculateOnlineSum(
  products: Record<string, any>,
  paymentMethods: Record<string, any>,
  orderStatus: string,
): number {
  let onlineSum = 0;
  const validPaymentIdentifiers: Array<string> = [];
  products.forEach((product: Record<string, any>) => {
    if ((orderStatus === "LOGISTIC_PROGRESS" || product.status !== "CANCELLED") && product.paid[0]) {
      validPaymentIdentifiers.push(product.paid[0].paymentMethodReference);
    }
  });
  paymentMethods.forEach((paymentMethod: Record<string, any>) => {
    // checks if payment is for non cancelled product and if payment method exists -> no payment on collection
    if (
      validPaymentIdentifiers.find(id => id === paymentMethod.identifier) &&
      Object.entries(paymentMethod.paymentMethod).length !== 0
    ) {
      onlineSum += paymentMethod.paidPrice.paidAmount.value;
    }
  });
  return onlineSum;
}

/**
 * renders sum with seperated click & collect sum if necessary
 * @param {Record<string, any>} order
 * @returns {TemplateResult}
 */
function renderSum(order: Record<string, any>): TemplateResult {
  let result = html``;
  const products = order.lines;
  const totalSum = calculateTotalSum(products, order.orderStatus);
  const hd = products.filter(
    (product: Record<string, any>) => product.deliveryReference === "delivery____domestichome",
  );
  // if only hd or only cc orders
  if (hd.length === 0 || hd.length === products.length) {
    result = html`
      <div class="fra-e-list__entry ">
        <dt class="fra-e-list__label">
          ${I18nService.getInstance().getKey("fraport.travel.components.orders.orderSum.label")}
        </dt>
        <dd class="fra-e-list__desc fra-e-list__desc-link ">
          ${formatPrice(totalSum, order.summary[0].gross.currency)}
        </dd>
      </div>
    `;
  } else {
    // if mixed cc and hd order
    const onlineSum = calculateOnlineSum(products, order.payment.methodsOfPayment, order.orderStatus);
    result = html`
      <div class="fra-e-list__entry ">
        <dt class="fra-e-list__label">
          ${I18nService.getInstance().getKey("fraport.travel.components.orders.onlineSum.label")}
        </dt>
        <dd class="fra-e-list__desc fra-e-list__desc-link ">
          ${formatPrice(onlineSum, order.summary[0].gross.currency)}
        </dd>
      </div>
      <div class="fra-e-list__entry ">
        <dt class="fra-e-list__label">
          ${I18nService.getInstance().getKey("fraport.travel.components.orders.offlineSum.label")}
        </dt>
        <dd class="fra-e-list__desc fra-e-list__desc-link ">
          ${formatPrice(totalSum - onlineSum, order.summary[0].gross.currency)}
        </dd>
      </div>
    `;
  }
  return result;
}

/**
 * renders old crossed through price
 * @param {number} price
 * @param {string} currency
 * @returns {TemplateResult}
 */
function renderOriginalPrice(price: number, currency: string): TemplateResult {
  const formattedPrice = formatPrice(price, currency);
  return html`
    <div class="fra-m-orders__product-info--original-price">${formattedPrice}</div>
  `;
}

/**
 * renders retailer name with link
 * @param {string} name
 * @param {string} retailerCode
 * @returns {TemplateResult}
 */
function renderRetailerName(name: string, retailerCode: string): TemplateResult {
  const url = `https://shop.frankfurt-airport.com/de-DE/retailer/${retailerCode}`;
  return html`
    <h2 class="fra-m-orders__details--headline fra-h1">
      ${I18nService.getInstance().getKey("fraport.travel.components.orders.delivery.soldBy")}
      <a href=${url} target="_blank" class="fra-m-orders__details--retailer-name">${name}</a>
    </h2>
  `;
}

/**
 * renders location with map link
 * @param {string} name
 * @param {Array} deliveries
 * @returns {TemplateResult}
 */
function renderCollectLocation(name: string, deliveries: Array<Record<string, any>>): TemplateResult {
  const location: Record<string, any> = deliveries.find(
    (location: Record<string, any>) => location.identifier === name,
  )!;
  if (!location) return html``;
  const attributes = location.method.pickup.address.customAttributes;
  const pickupLocation = `${
    location.method.pickup.pickupLocationText
      ? `${location.method.pickup.pickupLocationText}`
      : `"${attributes[0].value}" ${I18nService.getInstance().getKey(
          "fraport.travel.components.orders.delivery.altPickupLocationText",
        )}`
  }`;
  const pickupLocationMapUrl = location.method.pickup.pickupLocationMapOpenUrl;
  return html`
    <div class="fra-m-orders__pickup-info">
      <div>
        ${I18nService.getInstance().getKey("fraport.travel.components.orders.delivery.pickupLocation")}:
        ${pickupLocation}
      </div>
      ${pickupLocationMapUrl
        ? html`
            <a
              class="fra-e-link fra-e-link--animated-right fra-e-link--with-icon"
              href="${pickupLocationMapUrl}"
              target="_blank"
            >
              <span class="fra-e-link__label">
                ${I18nService.getInstance().getKey("fraport.travel.components.orders.delivery.showInMap")}
              </span>
              <fra-e-icon class="fra-e-icon " icon-id="fra-icon-arrow-right"></fra-e-icon>
            </a>
          `
        : ""}
    </div>
  `;
}

/**
 * extracts payment method
 * @param {Array} paymentMethods
 * @returns {string}
 */
function getPaymentMethod(paymentMethods: Array<Record<string, any>>): string {
  let paymentMethod = I18nService.getInstance().getKey("fraport.travel.components.orders.paymentMethod.notFound");
  paymentMethods.forEach(payment => {
    if (Object.keys(payment.paymentMethod)[0] !== undefined) {
      switch (Object.keys(payment.paymentMethod)[0]) {
        case "creditcard": {
          paymentMethod = I18nService.getInstance().getKey("fraport.travel.components.orders.paymentMethod.creditcard");
          break;
        }
        case "paypal": {
          paymentMethod = "Paypal";
          break;
        }
      }
    }
  });
  return paymentMethod;
}

/**
 * products with the same retailer are put into new retailer array
 * @param {Array} products
 * @returns {Array}
 */
function groupForRetailers(products: Array<Record<string, any>>): Array<Record<string, any>> {
  const retailers: Array<Record<string, any>> = [];
  products.forEach((productEntry: Record<string, any>) => {
    let found = false;
    retailers.forEach(entry => {
      if (Object.keys(entry).toString() === productEntry.product.retailerName) {
        const product = entry[productEntry.product.retailerName];
        product.push(productEntry);
        found = true;
      }
    });
    if (!found) retailers.push({ [productEntry.product.retailerName]: [productEntry] });
  });

  return retailers;
}

/**
 * render and group products
 * @param {Array} retailers
 * @param {Array} productArea
 * @param {string} orderStatus
 * @param {boolean} ccOrder
 * @param {Array} deliveries
 */
// eslint-disable-next-line max-params
function renderProductsWithAmount(
  retailers: Array<Record<string, any>>,
  productArea: Array<TemplateResult>,
  orderStatus: string,
  ccOrder: boolean,
  deliveries: [],
): void {
  retailers.forEach(retailer => {
    productArea.push(renderRetailerName(Object.keys(retailer).toString(), Object.values(retailer)[0][0].retailerCode));
    if (orderStatus !== "CANCELLED" && ccOrder) {
      productArea.push(renderCollectLocation(Object.values(retailer)[0][0].deliveryReference, deliveries));
    }
    // group equal products together
    const renderProductsWithAmount: Array<Record<string, any>> = [];
    Object.values(retailer)[0].forEach((product: Record<string, any>) => {
      const existingProduct = renderProductsWithAmount.find(
        productWithAmount =>
          productWithAmount.product.name === product.name && productWithAmount.product.status === product.status,
      );
      if (existingProduct) {
        existingProduct.amount++;
      } else {
        renderProductsWithAmount.push({ product, amount: 1 });
      }
    });
    // sorting for status
    renderProductsWithAmount.sort(a => {
      if (a.product.status === "COMPLETED") {
        return -1;
      }
      return 1;
    });
    renderProductsWithAmount.forEach(productWithAmount =>
      productArea.push(renderProduct(productWithAmount.product, orderStatus, productWithAmount.amount)),
    );
  });
}

/**
 * render products and group for delivery method
 * @param {Array} products
 * @param {string} orderStatus
 * @param {Array} deliveries
 * @returns {Array}
 */
function renderProducts(products: [], orderStatus: string, deliveries: []): Array<TemplateResult> {
  const productArea: Array<TemplateResult> = [];
  let hd = products.filter((product: Record<string, any>) => product.deliveryReference === "delivery____domestichome");
  const ccPassenger = products.filter((product: Record<string, any>) =>
    product.deliveryReference.startsWith("ispu_airside"),
  );
  const ccNonPassenger = products.filter((product: Record<string, any>) =>
    product.deliveryReference.startsWith("ispu_landside"),
  );
  const ccCrewshop = products.filter((product: Record<string, any>) =>
    product.deliveryReference.startsWith("ispu_staff"),
  );
  if (hd.length > 0) {
    // removes delivery cost entry
    hd = hd.filter((product: Record<string, any>) => product.product !== undefined);
    productArea.push(renderDeliveryMethod("hd"));
    const retailers = groupForRetailers(hd);
    renderProductsWithAmount(retailers, productArea, orderStatus, false, deliveries);
  }
  if (ccPassenger.length > 0) {
    productArea.push(renderDeliveryMethod("ccPassenger"));
    const retailers = groupForRetailers(ccPassenger);
    renderProductsWithAmount(retailers, productArea, orderStatus, true, deliveries);
  }
  if (ccNonPassenger.length > 0) {
    productArea.push(renderDeliveryMethod("ccNonPassenger"));
    const retailers = groupForRetailers(ccNonPassenger);
    renderProductsWithAmount(retailers, productArea, orderStatus, true, deliveries);
  }
  if (ccCrewshop.length > 0) {
    productArea.push(renderDeliveryMethod("ccCrewshop"));
    const retailers = groupForRetailers(ccCrewshop);
    renderProductsWithAmount(retailers, productArea, orderStatus, true, deliveries);
  }
  return productArea;
}

/**
 * renders date until order needs to be collected
 * @param {Record<string, any>} order
 * @returns {TemplateResult}
 */
function renderCollectDate(order: Record<string, any>): TemplateResult {
  // at least one cc passenger order
  const ccProducts = order.lines.filter((product: Record<string, any>) =>
    product.deliveryReference.startsWith("ispu_airside"),
  ).length;
  if (ccProducts > 0) {
    return html`
      <div class="fra-e-list__entry ">
        <dt class="fra-e-list__label">
          ${I18nService.getInstance().getKey("fraport.travel.components.orders.collectDate.label")}
        </dt>
        <dd class="fra-e-list__desc fra-e-list__desc-link ">${formatDate(order.flights[0].scheduledDate)}</dd>
      </div>
    `;
  }
  // only cc non passenger orders
  let crewshopOrder = false;
  const ccNonPassengerProducts = order.lines.filter((product: Record<string, any>) =>
    // eslint-disable-next-line prettier/prettier
    {
      if (product.deliveryReference.startsWith("ispu_staff")) {
        crewshopOrder = true;
      }
      return (
        product.deliveryReference.startsWith("ispu_landside") || product.deliveryReference.startsWith("ispu_staff")
      );
    },
  ).length;
  if (ccNonPassengerProducts === order.lines.length) {
    return html`
      <div class="fra-e-list__entry ">
        <dt class="fra-e-list__label">
          ${I18nService.getInstance().getKey("fraport.travel.components.orders.collectDatePeriod.label")}
        </dt>
        <dd class="fra-e-list__desc fra-e-list__desc-link ">
          ${I18nService.getInstance().getKey("fraport.travel.components.orders.collectDateUntil.label")}
          ${crewshopOrder ? increaseDate(order.orderDate, 14) : increaseDate(order.orderDate, 7)}
        </dd>
      </div>
    `;
  }

  return html``;
}

/**
 * Render status of an order
 * @param {string} state
 * @returns {TemplateResult}
 */
function renderState(state: string): TemplateResult {
  let statusMessage = "Unknown status";
  let color = "#687074";

  switch (state) {
    case "LOGISTIC_PROGRESS": {
      statusMessage = I18nService.getInstance().getKey("fraport.travel.components.orders.status.progressMessage");
      color = "#e4821a";
      break;
    }
    case "COMPLETED": {
      statusMessage = I18nService.getInstance().getKey("fraport.travel.components.orders.status.completedMessage");
      color = "#81ae31";
      break;
    }
    case "CANCELLED": {
      statusMessage = I18nService.getInstance().getKey("fraport.travel.components.orders.status.cancelledMessage");
      color = "#d71b3a";
      break;
    }
  }
  return html`
    <span class="fra-m-orders__state" style="color: ${color}">${statusMessage}</span>
  `;
}

/**
 * Render status of a product
 * @param {string} productStatus
 * @param {string} orderStatus
 * @param {string} deliveryReference
 * @returns {TemplateResult}
 */
function renderProductState(productStatus: string, orderStatus: string, deliveryReference: string): TemplateResult {
  let statusMessage = "";
  let color = "#687074";

  if (orderStatus === "CANCELLED") {
    statusMessage = I18nService.getInstance().getKey("fraport.travel.components.orders.productStatus.cancelledMessage");
    color = "#d71b3a";
  } else {
    switch (productStatus) {
      case "COMPLETED": {
        statusMessage = deliveryReference.includes("domestichome")
          ? I18nService.getInstance().getKey("fraport.travel.components.orders.productStatus.completedMessage")
          : I18nService.getInstance().getKey("fraport.travel.components.orders.productStatus.completedCCMessage");
        color = "#81ae31";
        break;
      }
      case "ORDERED": {
        statusMessage = I18nService.getInstance().getKey(
          "fraport.travel.components.orders.productStatus.orderedMessage",
        );
        color = "#e4821a";
        break;
      }
      case "CANCELLED": {
        statusMessage = I18nService.getInstance().getKey(
          "fraport.travel.components.orders.productStatus.cancelledMessage",
        );
        color = "#d71b3a";
        break;
      }
    }
  }
  if (!statusMessage) {
    return html``;
  }
  return html`
    Status:
    <span style="color: ${color}">${statusMessage}</span>
  `;
}

/**
 * render no orders available message
 * @returns {TemplateResult}
 */
export const noOrdersAvailable = (): TemplateResult => {
  return html`
    <div class="fra-m-orders__data-wrapper--no-orders-message">
      <fra-e-icon class="fra-e-icon fra-e-button__icon" icon-id="fra-icon-notification"></fra-e-icon>
      <div>${I18nService.getInstance().getKey("fraport.travel.components.orders.noOrdersMessage")}</div>
    </div>
  `;
};

/**
 * Render order items
 * @param {[Record<string, any>]} orders
 * @param {function} toggleLoadMoreButton
 * @param {number} showOrdersUpTo
 * @param {function} showOrderData
 * @returns {TemplateResult}
 */
export const ordersListTemplate = (
  orders: Record<string, any>[],
  toggleLoadMoreButton: Function,
  showOrdersUpTo: number,
  showOrderData: Function,
): TemplateResult => {
  if (orders.length > 0 && orders.length > showOrdersUpTo) {
    orders = orders.slice(0, showOrdersUpTo);
  }

  toggleLoadMoreButton(showOrdersUpTo);
  return html`
    ${orders.map(order => {
      return renderListEntry(
        formatDate(order.orderDate),
        order.orderNumber,
        formatPrice(order.totalAmount, order.currency),
        renderState(order.orderStatus),
        showOrderData,
      );
    })}
  `;
};

/**
 * render load more button if necessary
 * @param {Function} setState
 * @param {number} showOrdersUpTo
 * @returns {TemplateResult}
 */
export const loadMoreButton = (setState: Function, showOrdersUpTo: number): TemplateResult => {
  return html`
    <button type="button" class="fra-e-button" @click=${() => setState({ showOrdersUpTo: showOrdersUpTo + 20 })}>
      <span class="fra-e-button__label">
        ${I18nService.getInstance().getKey("fraport.travel.components.orders.loadMore.label")}
      </span>
    </button>
  `;
};

/**
 * Render order item
 * @param {Record<string, any>} order
 * @returns {TemplateResult}
 */
export const orderDetailsTemplate = (order: Record<string, any>): TemplateResult => {
  const hasHomeDelivery = order.deliveries.find((delivery: Record<string, any>) => delivery.method.addressDelivery);

  return html`
    <h2 class="fra-m-orders__details--headline fra-h1">
      ${I18nService.getInstance().getKey("fraport.travel.components.orders.orderNumber.label")} ${order.orderNumber}
    </h2>
    ${renderOrderDetailsList(order)}
      ${
        // eslint-disable-next-line no-nested-ternary
        hasHomeDelivery && order.orderStatus !== "CANCELLED"
          ? html`
              <div class="fra-grid-row fra-m-orders__data-wrapper">
                <div
                  class="fra-grid-col-mq1-12 fra-grid-col-mq2-5 fra-grid-col-mq3-4 fra-grid-col-mq4-3 fra-m-orders__data"
                >
                  ${renderAddress(order.billingAddress, "billing", order.orderStatus, order.invoiceUrl)}
                </div>
                <div
                  class="fra-grid-col-mq1-12 fra-grid-col-mq2-5 fra-grid-col-mq3-4 fra-grid-col-mq4-3 fra-m-orders__data"
                >
                  ${renderAddress(
                    hasHomeDelivery.method.addressDelivery.address,
                    "delivery",
                    order.orderStatus,
                    order.invoiceUrl,
                  )}
                </div>
              </div>
            `
          : order.orderStatus === "CANCELLED"
          ? html`
              <div class="fra-grid-row fra-m-orders__data-wrapper">${renderCancelledMessage()}</div>
            `
          : ""
      }
    </div>
    ${renderProducts(order.lines, order.orderStatus, order.deliveries)}
  `;
};
